/**
 * 应用级 UI 状态 —— 09 章 §9.10.1。
 *
 * 只放**跨页面且需要持久化**的东西。页面内部的展开/折叠、当前 Tab
 * 都留在组件本地（§9.10.2）——放进 store 只会让登出清理变得容易漏。
 */

import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'

const KEY_SIDEBAR = 'mtp.ui.sidebar_collapsed'
const KEY_THEME = 'mtp.ui.theme'

export type ThemeMode = 'light' | 'dark'

export const useAppStore = defineStore('app', () => {
  /** 用户**手动**选的折叠偏好。窄屏的强制折叠不写进这里（见 `sidebarCollapsed`）。 */
  const sidebarPreferCollapsed = ref(false)

  /**
   * 视口是否窄于 §9.3.3 的断点（1280px）。由 `AppLayout` 用 `matchMedia` 喂进来。
   *
   * **为什么不能只用 CSS 媒体查询**：那样只会把容器压到 64px，而 `el-menu`
   * 的 `:collapse` 绑的是折叠状态、不是容器宽度——菜单照常渲染完整中文标签，
   * 被裁成一列半个字（实测截图上是「系 服 数 模 训 任 创 评 部 监」，
   * 连 Logo 都被切成「灵 模」）。那不是"有点丑"，是导航直接不可用，
   * 而且 CSS 生效了、宽度也确实变了，**没有任何测试会因此变红**。
   */
  const viewportNarrow = ref(false)

  /** 窄屏下侧栏是否以浮层展开。宽屏无意义；切路由时由布局负责关掉。 */
  const narrowSidebarOpen = ref(false)

  /** 实际是否折叠：窄屏强制折叠成图标条（浮层展开时例外），否则听手动偏好。 */
  const sidebarCollapsed = computed(() =>
    viewportNarrow.value ? !narrowSidebarOpen.value : sidebarPreferCollapsed.value,
  )

  const theme = ref<ThemeMode>('light')

  /** 后端版本变化提示条（§9.13.6）。非空即显示"后端已更新，请刷新页面"。 */
  const backendVersionChanged = ref<{ from: string; to: string } | null>(null)

  function setViewportNarrow(narrow: boolean): void {
    viewportNarrow.value = narrow
  }

  function restoreSidebar(): void {
    sidebarPreferCollapsed.value = localStorage.getItem(KEY_SIDEBAR) === '1'
    const saved = localStorage.getItem(KEY_THEME)
    if (saved === 'dark' || saved === 'light') applyTheme(saved)
  }

  function toggleSidebar(): void {
    // 窄屏不能直接 return：那会让 1280px 以下按钮点了**毫无反应**，而静默失效比
    // 功能缺失更伤。
    //
    // 窄屏照常切换，只是由布局把侧栏渲染成浮层（覆盖内容而不是挤压内容）。
    // 仍然**不写偏好**：否则用户拖窄窗口时按一下，拖回宽屏后侧栏莫名其妙变了。
    if (viewportNarrow.value) {
      narrowSidebarOpen.value = !narrowSidebarOpen.value
      return
    }
    sidebarPreferCollapsed.value = !sidebarPreferCollapsed.value
  }

  function applyTheme(mode: ThemeMode): void {
    theme.value = mode
    document.documentElement.dataset['theme'] = mode
  }

  // 持久化**偏好**而不是当前折叠态：窄屏的强制折叠不该被记成用户的选择。
  watch(sidebarPreferCollapsed, (v) => localStorage.setItem(KEY_SIDEBAR, v ? '1' : '0'))
  watch(theme, (v) => localStorage.setItem(KEY_THEME, v))

  return {
    sidebarCollapsed,
    sidebarPreferCollapsed,
    viewportNarrow,
    narrowSidebarOpen,
    theme,
    backendVersionChanged,
    restoreSidebar,
    toggleSidebar,
    setViewportNarrow,
    applyTheme,
  }
})
